import { use, useEffect, useRef } from "react";

import { ContentLayoutContext } from "lib/layout/use-content-layout";
import { type Quote } from "lib/quotes/get-quotes";

import * as cssRenderer from "./css-renderer";
import * as domRenderer from "./dom-renderer";

export type HighlightTarget = {
  id: string;
  startXpointer: string;
  endXpointer: string;
  color: string;
};

const useCssHighlights = typeof CSS !== "undefined" && "highlights" in CSS;
const {
  renderHighlights,
  getQuoteIdAt,
  getQuoteElement,
  setQuoteColor,
  setPreviewColor,
  clearPreview,
} = useCssHighlights ? cssRenderer : domRenderer;

export {
  renderHighlights,
  getQuoteIdAt,
  getQuoteElement,
  setQuoteColor,
  setPreviewColor,
  clearPreview,
};

export function useHighlights(quotes: Quote[] | undefined) {
  const { layout } = use(ContentLayoutContext)!;
  const handle = useRef<{ clear(): void } | null>(null);

  useEffect(() => {
    if (quotes?.length && layout) {
      handle.current?.clear();

      const targets = quotes.map(q => ({
        id: q.id,
        startXpointer: q.start_xpointer,
        endXpointer: q.end_xpointer,
        color: q.color || "basic",
      }));

      handle.current = renderHighlights(targets);
    }

    return () => {
      handle.current?.clear();
    };
  }, [quotes, layout]);
}
